import { ButtonProps, TooltipProps } from 'lego-on-react';
import React, { useReducer, useState } from 'react';

import { BaseTooltipAction, TooltipState } from '../../types';
import TooltipButton from '../TooltipButton/TooltipButton';

interface ActionButtonProps<S extends TooltipState, A> {
    tooltipReducer: (state: S, action: BaseTooltipAction | (A extends void ? never : A)) => S;
    defaultTooltipState: S;
    children: React.ReactNode;
    action: () => Promise<BaseTooltipAction | (A extends void ? never : A) | void>;
    disabled?: boolean;
    theme?: ButtonProps['theme'];
    tooltipTo?: TooltipProps['to'];
}

export function ActionButton<TS extends TooltipState, A>({
    tooltipReducer,
    defaultTooltipState,
    children,
    action,
    disabled,
    theme,
    tooltipTo,
}: ActionButtonProps<TS, A>) {
    const [tooltipProps, dispatch] = useReducer(tooltipReducer, defaultTooltipState);
    const [isLoading, setIsLoading] = useState(false);

    const onClick = async () => {
        try {
            setIsLoading(true);
            const actionToDispatch = await action();

            dispatch(actionToDispatch || 'ok');
        } catch (_e) {
            dispatch('error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <TooltipButton
            {...tooltipProps}
            onOutsideClick={() => dispatch('clear')}
            onClick={onClick}
            progress={isLoading}
            disabled={disabled}
            theme={theme}
            tooltipTo={tooltipTo}
        >
            {children}
        </TooltipButton>
    );
}
